import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';
import { format, subDays, parseISO } from 'date-fns';
import { TimelineButtons } from '../components/TimelineButtons';
import {
    Cloud,
    Sun,
    CloudRain,
    Snowflake,
    Wind,
    Thermometer,
    Droplets,
    Gauge,
    Flower2,
    Loader2,
    Plus,
    Calendar,
    TrendingUp,
    Edit,
    Trash2,
    Save,
    ChevronLeft,
    ChevronRight,
    RefreshCw,
    MapPin,
    Moon
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WEATHER_ICONS = {
    'Sunny': Sun,
    'Partly Cloudy': Cloud,
    'Cloudy': Cloud,
    'Overcast': Cloud,
    'Light Rain': CloudRain,
    'Rain': CloudRain,
    'Heavy Rain': CloudRain,
    'Thunderstorm': CloudRain,
    'Snow': Snowflake,
    'Fog': Cloud,
    'Windy': Wind,
    'Hot': Thermometer,
    'Cold': Snowflake,
    'Humid': Droplets
};

const POLLEN_COLORS = {
    'Low': 'bg-emerald-500',
    'Medium': 'bg-amber-500',
    'High': 'bg-orange-500',
    'Very High': 'bg-rose-500'
};

const AQI_COLORS = {
    'Good': 'bg-emerald-500',
    'Moderate': 'bg-amber-500',
    'Unhealthy for Sensitive': 'bg-orange-500',
    'Unhealthy': 'bg-rose-500',
    'Very Unhealthy': 'bg-purple-500',
    'Hazardous': 'bg-red-900'
};

const MOON_PHASES = {
    'New Moon': { emoji: '🌑', color: 'bg-slate-700' },
    'Waxing Crescent': { emoji: '🌒', color: 'bg-slate-600' },
    'First Quarter': { emoji: '🌓', color: 'bg-slate-500' },
    'Waxing Gibbous': { emoji: '🌔', color: 'bg-yellow-600' },
    'Full Moon': { emoji: '🌕', color: 'bg-yellow-400' },
    'Waning Gibbous': { emoji: '🌖', color: 'bg-yellow-600' },
    'Last Quarter': { emoji: '🌗', color: 'bg-slate-500' },
    'Waning Crescent': { emoji: '🌘', color: 'bg-slate-600' }
};

export default function WeatherTracker() {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('log');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [entries, setEntries] = useState([]);
    const [options, setOptions] = useState(null);
    
    // Form state
    const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [temperature, setTemperature] = useState('');
    const [humidity, setHumidity] = useState([50]);
    const [pressure, setPressure] = useState('');
    const [conditions, setConditions] = useState('');
    const [pollenLevel, setPollenLevel] = useState('');
    const [airQuality, setAirQuality] = useState('');
    const [notes, setNotes] = useState('');
    
    // For history view
    const [historyRange, setHistoryRange] = useState(14);
    
    // Auto-fetch state
    const [autoFetching, setAutoFetching] = useState(false);
    const [currentEntryAutoFetched, setCurrentEntryAutoFetched] = useState(false);
    const [currentEntryCity, setCurrentEntryCity] = useState('');
    
    // Zipcode state
    const [zipcode, setZipcode] = useState('');
    const [savedZipcode, setSavedZipcode] = useState('');
    
    // Moon phase state
    const [moonPhase, setMoonPhase] = useState(null);
    const [loadingMoon, setLoadingMoon] = useState(false);

    const fetchOptions = useCallback(async () => {
        try {
            const res = await axios.get(`${API_URL}/api/weather/options/all`);
            setOptions(res.data);
        } catch (error) {
            console.error('Failed to fetch options:', error);
        }
    }, []);

    const fetchEntries = useCallback(async () => {
        try {
            const startDate = format(subDays(new Date(), historyRange), 'yyyy-MM-dd');
            const res = await axios.get(`${API_URL}/api/weather`, {
                params: { start_date: startDate }
            });
            setEntries(res.data);
        } catch (error) {
            console.error('Failed to fetch entries:', error);
        }
    }, [historyRange]);

    const fetchMoonPhase = useCallback(async (date) => {
        setLoadingMoon(true);
        try {
            const res = await axios.get(`${API_URL}/api/weather/moon-phase`, {
                params: { date }
            });
            console.log('[WeatherTracker] Moon phase:', res.data);
            setMoonPhase(res.data);
        } catch (error) {
            console.error('Failed to fetch moon phase:', error);
        } finally {
            setLoadingMoon(false);
        }
    }, []);

    const loadEntryForDate = useCallback(async (date) => {
        try {
            const res = await axios.get(`${API_URL}/api/weather/${date}`);
            const entry = res.data;
            setTemperature(entry.temperature?.toString() || '');
            setHumidity([entry.humidity || 50]);
            setPressure(entry.pressure?.toString() || '');
            setConditions(entry.conditions || '');
            setPollenLevel(entry.pollen_level || '');
            setAirQuality(entry.air_quality || '');
            setNotes(entry.notes || '');
            setCurrentEntryAutoFetched(entry.auto_fetched || false);
            setCurrentEntryCity(entry.location?.city || '');
        } catch (error) {
            // No entry for this date - reset form
            setTemperature('');
            setHumidity([50]);
            setPressure('');
            setConditions('');
            setPollenLevel('');
            setAirQuality('');
            setNotes('');
            setCurrentEntryAutoFetched(false);
            setCurrentEntryCity('');
        }
    }, []);

    const handleAutoFetchWeather = async () => {
        if (!navigator.geolocation) {
            toast.error('Geolocation is not supported by your browser');
            return;
        }
        
        setAutoFetching(true);
        
        navigator.geolocation.getCurrentPosition(
            async (position) => {
                try {
                    const { latitude, longitude } = position.coords;
                    const response = await axios.post(`${API_URL}/api/weather/auto-fetch`, {
                        latitude,
                        longitude
                    });
                    
                    if (response.data.already_fetched) {
                        toast.info('Weather already fetched for today');
                    } else {
                        toast.success(`Weather fetched for ${response.data.data?.city || 'your location'}`);
                    }
                    
                    // Reload the entry for the selected date
                    await loadEntryForDate(selectedDate);
                    await fetchEntries();
                } catch (error) {
                    toast.error(error.response?.data?.detail || 'Failed to fetch weather');
                } finally {
                    setAutoFetching(false);
                }
            },
            (error) => {
                toast.error('Location access denied. Please enable location permissions or enter your zipcode.');
                setAutoFetching(false);
            },
            { timeout: 10000 }
        );
    };

    const handleFetchByZipcode = async () => {
        if (!zipcode || zipcode.length < 5) {
            toast.error('Please enter a valid 5-digit zipcode');
            return;
        }
        
        setAutoFetching(true);
        
        try {
            const response = await axios.post(`${API_URL}/api/weather/fetch-by-zipcode`, {
                zipcode: zipcode,
                country_code: 'US'
            });
            
            toast.success(`Weather fetched for ${response.data.data?.city || zipcode}`);
            setSavedZipcode(zipcode);
            
            // Reload the entry for the selected date
            await loadEntryForDate(selectedDate);
            await fetchEntries();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to fetch weather. Please check your zipcode.');
        } finally {
            setAutoFetching(false);
        }
    };

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await Promise.all([fetchOptions(), fetchEntries(), fetchWeatherPreferences()]);
            await loadEntryForDate(selectedDate);
            await fetchMoonPhase(selectedDate);
            setLoading(false);
        };
        loadData();
    }, [fetchOptions, fetchEntries, fetchMoonPhase, loadEntryForDate, selectedDate]);

    const fetchWeatherPreferences = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/user/weather-preferences`);
            setSavedZipcode(res.data.zipcode || '');
            setZipcode(res.data.zipcode || '');
        } catch (error) {
            console.log('No saved weather preferences');
        }
    };

    const handleDateChange = (direction) => {
        const current = parseISO(selectedDate);
        const newDate = direction === 'prev' 
            ? subDays(current, 1)
            : new Date(current.getTime() + 24 * 60 * 60 * 1000);
        const dateStr = format(newDate, 'yyyy-MM-dd');
        setSelectedDate(dateStr);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await axios.post(`${API_URL}/api/weather`, {
                date: selectedDate,
                temperature: temperature ? parseFloat(temperature) : null,
                humidity: humidity[0],
                pressure: pressure ? parseFloat(pressure) : null,
                conditions: conditions || null,
                pollen_level: pollenLevel || null,
                air_quality: airQuality || null,
                notes: notes || null
            });
            
            toast.success('Weather data saved!');
            fetchEntries();
        } catch (error) {
            toast.error('Failed to save weather data');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (date) => {
        try {
            await axios.delete(`${API_URL}/api/weather/${date}`);
            toast.success('Entry deleted');
            fetchEntries();
            if (date === selectedDate) {
                loadEntryForDate(selectedDate);
            }
        } catch (error) {
            toast.error('Failed to delete entry');
        }
    };

    const getConditionIcon = (condition) => {
        const Icon = WEATHER_ICONS[condition] || Cloud;
        return <Icon className="w-5 h-5" />;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                        <Cloud className="w-8 h-8 text-sky-500" />
                        Weather Tracker
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Track weather conditions to discover how they affect your symptoms
                    </p>
                </div>
                <TimelineButtons />
            </div>

            {/* Auto-Fetch Explanation Card */}
            <Card className="border-emerald-200 dark:border-emerald-800 bg-gradient-to-r from-emerald-50/50 to-sky-50/50 dark:from-emerald-950/20 dark:to-sky-950/20">
                <CardContent className="pt-6">
                    <div className="flex gap-4">
                        <div className="flex-shrink-0">
                            <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center">
                                <RefreshCw className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <h3 className="font-semibold text-emerald-800 dark:text-emerald-200">
                                Automatic Weather Tracking
                            </h3>
                            <p className="text-sm text-muted-foreground">
                                <strong>Good news!</strong> Weather data is automatically fetched each day when you open the app, 
                                based on your saved zipcode. This data flows into your <strong>Smart Correlations</strong> and 
                                <strong> Flare Predictions</strong> to help identify weather-related patterns in your symptoms.
                            </p>
                            <p className="text-sm text-muted-foreground">
                                The manual options below are available if you're in a <strong>different location</strong> or want to 
                                add additional details like <strong>pollen levels</strong> that aren't available from automatic fetching.
                            </p>
                            {savedZipcode && (
                                <p className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
                                    ✓ Auto-fetch enabled for zipcode: {savedZipcode}
                                </p>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList>
                    <TabsTrigger value="log" className="gap-2">
                        <Plus className="w-4 h-4" />
                        Log Weather
                    </TabsTrigger>
                    <TabsTrigger value="history" className="gap-2">
                        <Calendar className="w-4 h-4" />
                        History
                    </TabsTrigger>
                    <TabsTrigger value="insights" className="gap-2">
                        <TrendingUp className="w-4 h-4" />
                        Insights
                    </TabsTrigger>
                </TabsList>

                {/* Log Weather Tab */}
                <TabsContent value="log" className="mt-6">
                    {/* Zipcode Input Card */}
                    <Card className="mb-6 border-sky-200 dark:border-sky-800 bg-sky-50/50 dark:bg-sky-950/20">
                        <CardContent className="pt-6">
                            <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
                                <div className="flex-1">
                                    <Label className="flex items-center gap-2 mb-2">
                                        <MapPin className="w-4 h-4 text-sky-500" />
                                        Your Zipcode
                                    </Label>
                                    <div className="flex gap-2">
                                        <Input
                                            type="text"
                                            placeholder="Enter zipcode (e.g., 90210)"
                                            value={zipcode}
                                            onChange={(e) => setZipcode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                                            className="w-40"
                                            maxLength={5}
                                        />
                                        <Button 
                                            onClick={handleFetchByZipcode}
                                            disabled={autoFetching || zipcode.length < 5}
                                            className="gap-2 bg-sky-500 hover:bg-sky-600"
                                        >
                                            {autoFetching ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <RefreshCw className="w-4 h-4" />
                                            )}
                                            Fetch Weather
                                        </Button>
                                    </div>
                                    {savedZipcode && (
                                        <p className="text-xs text-muted-foreground mt-1">
                                            Saved zipcode: {savedZipcode}
                                        </p>
                                    )}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                    or{' '}
                                    <button 
                                        onClick={handleAutoFetchWeather}
                                        disabled={autoFetching}
                                        className="text-sky-500 hover:text-sky-600 underline"
                                    >
                                        use my current location
                                    </button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <div className="flex items-center justify-between flex-wrap gap-4">
                                <div>
                                    <CardTitle className="flex items-center gap-2">
                                        Log Weather Conditions
                                        {currentEntryAutoFetched && (
                                            <Badge variant="outline" className="ml-2 bg-sky-500/10 text-sky-600 border-sky-500/30">
                                                <MapPin className="w-3 h-3 mr-1" />
                                                Auto-fetched{currentEntryCity ? ` from ${currentEntryCity}` : ''}
                                            </Badge>
                                        )}
                                    </CardTitle>
                                    <CardDescription>Record today's weather to find correlations with your symptoms</CardDescription>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 mt-4">
                                <Button variant="outline" size="icon" onClick={() => handleDateChange('prev')}>
                                    <ChevronLeft className="w-4 h-4" />
                                </Button>
                                <Input
                                    type="date"
                                    value={selectedDate}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                    className="w-40"
                                />
                                <Button 
                                    variant="outline" 
                                    size="icon" 
                                    onClick={() => handleDateChange('next')}
                                    disabled={selectedDate >= format(new Date(), 'yyyy-MM-dd')}
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="grid gap-6 md:grid-cols-2">
                                {/* Temperature */}
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        <Thermometer className="w-4 h-4 text-rose-500" />
                                        Temperature (°F)
                                    </Label>
                                    <Input
                                        type="number"
                                        placeholder="72"
                                        value={temperature}
                                        onChange={(e) => setTemperature(e.target.value)}
                                    />
                                </div>

                                {/* Barometric Pressure */}
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        <Gauge className="w-4 h-4 text-indigo-500" />
                                        Barometric Pressure (inHg)
                                    </Label>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        placeholder="29.92"
                                        value={pressure}
                                        onChange={(e) => setPressure(e.target.value)}
                                    />
                                </div>
                            </div>

                            {/* Humidity Slider */}
                            <div className="space-y-4">
                                <Label className="flex items-center gap-2">
                                    <Droplets className="w-4 h-4 text-sky-500" />
                                    Humidity: {humidity[0]}%
                                </Label>
                                <Slider
                                    value={humidity}
                                    onValueChange={setHumidity}
                                    max={100}
                                    step={5}
                                    className="w-full"
                                />
                                <div className="flex justify-between text-xs text-muted-foreground">
                                    <span>Dry (0%)</span>
                                    <span>Humid (100%)</span>
                                </div>
                            </div>

                            {/* Weather Conditions */}
                            <div className="space-y-2">
                                <Label className="flex items-center gap-2">
                                    <Sun className="w-4 h-4 text-amber-500" />
                                    Weather Conditions
                                </Label>
                                <Select value={conditions} onValueChange={setConditions}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select conditions" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {options?.conditions?.map((condition) => (
                                            <SelectItem key={condition} value={condition}>
                                                <div className="flex items-center gap-2">
                                                    {getConditionIcon(condition)}
                                                    {condition}
                                                </div>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="grid gap-6 md:grid-cols-2">
                                {/* Pollen Level */}
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        <Flower2 className="w-4 h-4 text-amber-500" />
                                        Pollen Level
                                    </Label>
                                    <Select value={pollenLevel} onValueChange={setPollenLevel}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select level" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {options?.pollen_levels?.map((level) => (
                                                <SelectItem key={level} value={level}>
                                                    <div className="flex items-center gap-2">
                                                        <div className={`w-3 h-3 rounded-full ${POLLEN_COLORS[level]}`} />
                                                        {level}
                                                    </div>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Air Quality */}
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        <Wind className="w-4 h-4 text-slate-500" />
                                        Air Quality
                                    </Label>
                                    <Select value={airQuality} onValueChange={setAirQuality}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select quality" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {options?.air_quality_levels?.map((level) => (
                                                <SelectItem key={level} value={level}>
                                                    <div className="flex items-center gap-2">
                                                        <div className={`w-3 h-3 rounded-full ${AQI_COLORS[level]}`} />
                                                        {level}
                                                    </div>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            {/* Moon Phase Card */}
                            <Card className="bg-gradient-to-br from-slate-900 to-slate-800 border-slate-700">
                                <CardContent className="pt-6">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl ${moonPhase ? MOON_PHASES[moonPhase.phase]?.color || 'bg-slate-600' : 'bg-slate-600'}`}>
                                                {loadingMoon ? (
                                                    <Loader2 className="w-6 h-6 animate-spin text-white" />
                                                ) : (
                                                    moonPhase?.emoji || '🌙'
                                                )}
                                            </div>
                                            <div>
                                                <p className="text-sm text-slate-400">Moon Phase</p>
                                                <p className="font-semibold text-white">
                                                    {loadingMoon ? 'Loading...' : moonPhase?.phase || 'Unknown'}
                                                </p>
                                            </div>
                                        </div>
                                        {moonPhase && !loadingMoon && (
                                            <div className="text-right">
                                                <p className="text-sm text-slate-400">Illumination</p>
                                                <p className="font-semibold text-white">{moonPhase.illumination}%</p>
                                            </div>
                                        )}
                                    </div>
                                    {moonPhase && !loadingMoon && (
                                        <div className="mt-4 pt-4 border-t border-slate-700 grid grid-cols-2 gap-4">
                                            <div>
                                                <p className="text-xs text-slate-500">Days to Full Moon</p>
                                                <p className="text-sm text-slate-300">{moonPhase.days_to_full_moon} days</p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-slate-500">Days to New Moon</p>
                                                <p className="text-sm text-slate-300">{moonPhase.days_to_new_moon} days</p>
                                            </div>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Notes */}
                            <div className="space-y-2">
                                <Label>Notes</Label>
                                <Textarea
                                    placeholder="Any additional observations about the weather..."
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    rows={3}
                                />
                            </div>

                            <Button onClick={handleSave} disabled={saving} className="w-full gap-2">
                                {saving ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Save className="w-4 h-4" />
                                )}
                                Save Weather Data
                            </Button>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* History Tab */}
                <TabsContent value="history" className="mt-6">
                    <Card>
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <CardTitle>Weather History</CardTitle>
                                <Select value={historyRange.toString()} onValueChange={(v) => setHistoryRange(parseInt(v))}>
                                    <SelectTrigger className="w-40">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="7">Last 7 days</SelectItem>
                                        <SelectItem value="14">Last 14 days</SelectItem>
                                        <SelectItem value="30">Last 30 days</SelectItem>
                                        <SelectItem value="90">Last 90 days</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {entries.length === 0 ? (
                                <div className="text-center py-12">
                                    <Cloud className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
                                    <p className="text-muted-foreground">No weather entries yet</p>
                                    <Button variant="link" onClick={() => setActiveTab('log')}>
                                        Log your first entry
                                    </Button>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {entries.map((entry) => (
                                        <div
                                            key={entry.id}
                                            className="p-4 rounded-lg border bg-card hover:bg-muted/30 transition-colors"
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-4">
                                                    <div className="text-center">
                                                        <p className="text-sm font-medium">
                                                            {format(parseISO(entry.date), 'MMM d')}
                                                        </p>
                                                        <p className="text-xs text-muted-foreground">
                                                            {format(parseISO(entry.date), 'EEE')}
                                                        </p>
                                                    </div>
                                                    
                                                    {entry.conditions && (
                                                        <div className="flex items-center gap-2">
                                                            {getConditionIcon(entry.conditions)}
                                                            <span className="text-sm">{entry.conditions}</span>
                                                        </div>
                                                    )}
                                                    
                                                    {entry.temperature && (
                                                        <Badge variant="outline" className="gap-1">
                                                            <Thermometer className="w-3 h-3" />
                                                            {entry.temperature}°F
                                                        </Badge>
                                                    )}
                                                    
                                                    {entry.humidity && (
                                                        <Badge variant="outline" className="gap-1">
                                                            <Droplets className="w-3 h-3" />
                                                            {entry.humidity}%
                                                        </Badge>
                                                    )}
                                                    
                                                    {entry.pollen_level && (
                                                        <Badge className={`${POLLEN_COLORS[entry.pollen_level]} text-white`}>
                                                            Pollen: {entry.pollen_level}
                                                        </Badge>
                                                    )}
                                                </div>
                                                
                                                <div className="flex items-center gap-2">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => {
                                                            setSelectedDate(entry.date);
                                                            setActiveTab('log');
                                                        }}
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="text-destructive"
                                                        onClick={() => handleDelete(entry.date)}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                            {entry.notes && (
                                                <p className="text-sm text-muted-foreground mt-2 pl-16">
                                                    {entry.notes}
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Insights Tab */}
                <TabsContent value="insights" className="mt-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Weather Insights</CardTitle>
                            <CardDescription>
                                How weather patterns correlate with your symptoms
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="text-center py-12">
                                <TrendingUp className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
                                <p className="text-muted-foreground mb-2">
                                    Weather insights will appear here once you have more data
                                </p>
                                <p className="text-sm text-muted-foreground">
                                    Log weather conditions regularly to discover patterns with your symptoms.
                                    Check the <strong>Smart Correlations</strong> feature on your Medical Timeline for AI-powered insights.
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
}
